<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core';
import { Users, Tags } from '@ui/icons/regular';
import { useCompanyStore } from '@sub/stores/company';
import { type IService, ServiceFormats, ServiceTypes } from '@types';
import PriceModal from '@sub/components/service/PriceModal.vue';
import { getServiceImages } from '@sub/utils/images';
import { getPlacesData } from '@sub/utils/seats';
import api from '@sub/api/api';
import { joinPageTitle, usePageTitle } from '@sub/utils/usePageSettings';

const store = useCompanyStore();
const { company, companyInfo } = storeToRefs(store);

const route = useRoute();
const serviceId = +route.params.id;

const { data: service, status } = useAsyncData(`${serviceId}`, async() => {
  const companyId = await store.getCompanyIdByAlias();
  if (!companyId || !serviceId) navigateTo('/404');

  const cachedService = store.services.main.find(s => s.id === serviceId);
  if (cachedService) return cachedService;

  try {
    const [, service] = await Promise.all([
      store.fetchCompany(companyId),
      api.services.getService(companyId, serviceId),
    ]);
    return service;
  } catch {
    navigateTo('/404');
    return {};
  }
}, { default: () => ({} as IService) });

usePageTitle(computed(() => joinPageTitle(company.value.clientName, service.value?.title)));

const images = computed(() => getServiceImages(service.value));

const openPrices = ref(false);
const showViewer = ref(false);

const seats = computed(() => {
  const { min: from, max: to, includedSeats } = getPlacesData(service.value);
  const capacitySeats = service.value.type === ServiceTypes.Individual && from !== to ? ['word.place_diapason', {from, to}] : ['word.place', to];
  return [
    { label: 'block.service.info.page.seats.capacity.title', seats: capacitySeats, show: !!to },
    { label: 'block.service.info.page.seats.included.title', seats: ['word.place', includedSeats], show: !!includedSeats },
  ].filter(s => s.show);
});

const free = computed(() => service.value.format === ServiceFormats.without);

useSeoMeta({
  description: service.value.description,
  ogTitle: service.value.title,
  ogDescription: service.value.description,
  ogImage: images.value[0],
  twitterTitle: service.value.title,
  twitterDescription: service.value.description,
  twitterImage: images.value[0],
  twitterCard: 'summary',
});

definePageMeta({
  keepalive: true,
  key: route => route.fullPath,
});

const showPriceSettings = computed(() => companyInfo.value.settings.showPriceSettings && !free.value);
const isMobileScreen = useMediaQuery('(max-width: 799px)')
</script>

<template lang="pug">
UiLoaderCircleLoader.m-auto(v-if="status === 'pending'" title="page.services.loader")
template(v-else-if="company?.id")
  ServicePageHeader(label="page.services.info.service.header.title" back="/services" :logoSrc="company.logoUrl")
  .content.flex.row-24.overflow-hidden
    UiImage.main-logo.size-216.desktop(:src="images[0]" :border="false" :zoom="!!images[0]" :placeholder="company.logoUrl" @click="images[0] && (showViewer = true)")
    .flex-space-between-start.col-0.w-100p
      .flex.col-10.w-100p
        .title.typ-20-28-600 {{ service.title }}
        .title.typ-14-18-400.m-t-6 {{ service.addresses?.[0]?.country }}
        UiImageSlider.p-8-0(:src="images" :show="isMobileScreen")
        ServiceTimeDuration.title.typ-14-18-400.m-b-6(v-if="companyInfo.settings.showServiceDuration" :service="service")
      .flex.col-20.p-16-0-0
        //.time.flex.row-16(v-if="false")
        //  UiIconsIcon.size-16.m-t-1(:name="Clock")
        //  .flex.col-2.typ-14-18-400
        //    span Пн – Пт  09:00 – 14:00
        //    span Сб – Вс  10:00 – 18:00
        .capacity.flex.row-16
          UiIconsIcon.size-16.m-t-1(:name="Users")
          .flex.col-2.typ-14-18-300
            .flex.row-4(v-for="seat of seats" :key="seat.label")
              span {{ $t(seat.label) }}:
              span.title {{ $t(...seat.seats) }}
        p.description.typ-13-17-300(v-if="service.description") {{ service.description }}
        .actions.flex
          UiButton(v-if="showPriceSettings" :icon="Tags" size="small" hierarchy="secondary" kind="outline" label="block.service.info.page.button.price.text" @click="openPrices = true")
          ServiceBookButton(hierarchy="primary" :service="service" :showServicePrice="companyInfo.settings.showMinServicePrice")

PriceModal(v-if="showPriceSettings" v-model:show="openPrices" :service="service")
UiImageViewer(v-model:show="showViewer" :src="images")
</template>

<style scoped lang="scss">
.title {
  color: #1F1F1F;
}

.time,
.capacity {
  color: #5B646D;

  .icon {
    color: #818D98;
  }
}

.desktop {
  display: none;

  @screen md {
    display: unset;
  }
}

.content {
  --uno: col-24 p-16-16-20;

  @screen sm {
    --uno: p-20-24;
  }

  @screen md {
    --uno: row-24;
  }
}

.description {
  white-space: pre-line;
  color: #5B646D;
  margin: 0;
}

.main-logo {
  display: none;
  flex-shrink: 0;

  @screen md {
    display: unset;
  }
}

.actions {
  --uno: row-12;
  white-space: nowrap;

  @screen sm {
    --uno: row-24;
  }
}
</style>